import qs from 'qs';
import { axiosClient } from '@/libs/axios-client';
import {
  customUrlEncode,
  prepareFilters,
  preparePagination,
  prepareSorters,
} from '@/utils/apiParamsUtils';

/**
 * Creates a new company with the provided data.
 * @param {Object} payload - The data for the new company.
 * @returns {Promise<Object>} A promise that resolves with the created company data.
 * @throws Will throw an error if the creation fails.
 */
export const createNewCompany = async (payload) => {
  try {
    const response = await axiosClient.post('/companies/create', payload);
    return response.data;
  } catch (error) {
    console.error('Failed to create company:', error);
    throw error;
  }
};

/**
 * Retrieves all companies.
 * @returns {Promise<Object[]>} A promise that resolves with an array of company objects.
 * @throws Will throw an error if the request fails.
 */
export const retrieveAllCompanies = async (params) => {
  const filters = prepareFilters(params, {
    // Table filters
    _id: 'array',
    'generalData.address': 'default',
    'clientsAdmins.clients': 'default',
    'clientsAdmins.companyAdmins': 'default',
    // Global filters
    createdAt: 'range',
    updatedAt: 'range',
    'applicationSettings.eInvoice': 'default',
    'applicationSettings.foreignWorkPermitTracker': 'default',
    'applicationSettings.holidayDaysTracker': 'default',
    'applicationSettings.isForEmployees': 'default',
    'applicationSettings.isHr': 'default',
    'applicationSettings.isSector': 'default',
    'applicationSettings.typeOfCompany': 'default',
    market: 'default',
    'clientsAdmins.clientOfficers': 'default',
    'clientsAdmins.eInvoiceOfficers': 'default',
    'generalData.city': 'default',
    'generalData.code': 'default',
    'generalData.email': 'default',
    'generalData.pib': 'default',
  });

  const sorters = prepareSorters(params, {
    company: 'sortCompany',
    address: 'sortAddress',
    clients: 'sortClients',
    companyAdmin: 'sortCompanyAdmins',
    createdAt: 'sortCreatedAt',
    updatedAt: 'sortUpdatedAt',
    _id: 'sort_id',
  });

  const pagination = preparePagination(params);

  try {
    const response = await axiosClient.get('/companies', {
      params: {
        filter: filters,
        sorter: sorters,
        ...pagination,
      },
      paramsSerializer: (params) => {
        if (params.filter) {
          params.filter = JSON.stringify(params.filter);
        }

        if (params.sorter) {
          params.sorter = JSON.stringify(params.sorter);
        }

        return qs.stringify(params, {
          encode: true,
          arrayFormat: 'brackets',
          encoder: customUrlEncode,
        });
      },
    });
    return response.data;
  } catch (error) {
    console.error('Failed to retrieve companies:', error);
    throw error;
  }
};

/**
 * Retrieves dropdown all companies.
 * @returns {Promise<Object[]>} A promise that resolves with an array of company objects.
 * @throws Will throw an error if the request fails.
 */
export const retrieveDropdownAllCompanies = async (params) => {
  try {
    const response = await axiosClient.get('/companies/dropdown', {
      params: {
        filter: params?.filter,
        select: params?.select,
        graph: params?.graph,
      },
      paramsSerializer: (params) => {
        if (params.filter) {
          params.filter = JSON.stringify(params.filter);
        }

        if (params.graph) {
          params.graph = JSON.stringify(params.graph);
        }

        if (params.select) {
          params.select = JSON.stringify(params.select);
        }

        return qs.stringify(params, { encode: false, arrayFormat: 'brackets' });
      },
    });

    return response.data;
  } catch (error) {
    console.error('Failed to retrieve dropdown companies:', error);
    throw error;
  }
};

/**
 * Retrieves a company by its ID with optional query parameters.
 *
 * @param {string} companyId - The unique identifier of the company.
 * @param {Object} [params] - Optional query parameters.
 * @param {Object} [params.graph] - Graph query object, which will be stringified.
 * @returns {Promise<Object>} A promise that resolves with the company data.
 * @throws Will throw an error if the request fails.
 */
export const getCompanyById = async (companyId, params) => {
  try {
    const response = await axiosClient.get(`/companies/${companyId}`, {
      params,
      paramsSerializer: (params) => {
        if (params.graph) {
          params.graph = JSON.stringify(params.graph);
        }

        return qs.stringify(params, { encode: false, arrayFormat: 'brackets' });
      },
    });
    const { data } = response.data;
    return data;
  } catch (error) {
    console.error(`Failed to retrieve company with ID ${companyId}:`, error);
    throw error;
  }
};

/**
 * Updates an existing company by its ID.
 * @param {string} companyId - The unique identifier of the company.
 * @param {Object} payload - The updated data for the company.
 * @returns {Promise<Object>} A promise that resolves with the updated company data.
 * @throws Will throw an error if the update fails.
 */
export const updateCompanyById = async (companyId, payload) => {
  try {
    const response = await axiosClient.put(`/companies/${companyId}`, payload);
    return response.data;
  } catch (error) {
    console.error(`Failed to update company with ID ${companyId}:`, error);
    throw error;
  }
};

/**
 * Deletes a company by its ID.
 * @param {string} companyId - The unique identifier of the company.
 * @returns {Promise<Object>} A promise that resolves when the company is deleted.
 * @throws Will throw an error if the deletion fails.
 */
export const deleteCompanyById = async (companyId) => {
  try {
    const response = await axiosClient.delete(`/companies/${companyId}`);
    return response.data;
  } catch (error) {
    console.error(`Failed to delete company with ID ${companyId}:`, error);
    throw error;
  }
};

/**
 * Creates or updates a questionnaire for a company.
 * @param {Object} payload - The questionnaire data.
 * @returns {Promise<Object>} A promise that resolves with the questionnaire data.
 * @throws Will throw an error if the creation/update fails.
 */
export const createQuestionnaire = async (payload) => {
  try {
    const response = await axiosClient.post(
      '/companies/questionnaire',
      payload
    );
    return response.data;
  } catch (error) {
    console.error('Failed to create/update questionnaire:', error);
    throw error;
  }
};

/**
 * Downloads questionnaire PDF for a company.
 * @param {string} companyId - The unique identifier of the company.
 * @param {string} language - The language code (sr, en, ru).
 * @returns {Promise<void>} Downloads the PDF file.
 * @throws Will throw an error if the download fails.
 */
export const downloadQuestionnairePdf = async (companyId, language) => {
  try {
    const response = await axiosClient.get(
      `/companies/${companyId}/questionnaire/pdf`,
      {
        params: { language },
        responseType: 'blob',
      }
    );

    // Extract filename from Content-Disposition header or use default
    const contentDisposition = response.headers['content-disposition'];
    let filename = `questionnaire-${language}.pdf`;
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="?(.+)"?/);
      if (filenameMatch) {
        const [, extractedFilename] = filenameMatch;
        if (extractedFilename) {
          filename = extractedFilename;
        }
      }
    }

    // Create blob and download
    const blob = new Blob([response.data], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Failed to download questionnaire PDF:', error);
    throw error;
  }
};

// With responseType 'blob' the JSON error body arrives as a Blob too, so the
// forms never saw the server's list of missing fields — only a generic failure.
const unwrapBlobError = async (error) => {
  const data = error?.response?.data;
  if (data instanceof Blob && data.type.includes('application/json')) {
    try {
      error.response.data = JSON.parse(await data.text());
    } catch {
      // keep the original body when it is not valid JSON
    }
  }
};

/**
 * Creates statuary documents (PEP, CROSO, PoA) for a company.
 * @param {Object} payload - The statuary documents data.
 * @returns {Promise<Blob>} A promise that resolves with the ZIP file blob.
 * @throws Will throw an error if the creation fails.
 */
export const createStatuaryDocuments = async (payload) => {
  try {
    const response = await axiosClient.post('/companies/statuary', payload, {
      responseType: 'blob',
    });
    return response.data;
  } catch (error) {
    await unwrapBlobError(error);
    console.error('Failed to create statuary documents:', error);
    throw error;
  }
};

/**
 * Creates functionary/ownership documents (SPNFT 1, 2, 3) for a company.
 * @param {Object} payload - The functionary/ownership documents data.
 * @returns {Promise<Blob>} A promise that resolves with the ZIP file blob.
 * @throws Will throw an error if the creation fails.
 */
export const createFunctionaryOwnershipDocuments = async (payload) => {
  try {
    const response = await axiosClient.post(
      '/companies/functionary-ownership-docs',
      payload,
      {
        responseType: 'blob',
      }
    );
    return response.data;
  } catch (error) {
    await unwrapBlobError(error);
    console.error('Failed to create functionary/ownership documents:', error);
    throw error;
  }
};

/**
 * Upload and translate salary PDF document
 * @param {FormData} formData - FormData containing the PDF file
 * @returns {Promise<Blob>} A promise that resolves with the translated PDF blob
 * @throws Will throw an error if the upload/translation fails
 */
export const uploadSalaryTranslation = async (formData) => {
  try {
    const response = await axiosClient.post('/companies/upload-salary-translation', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      responseType: 'blob',
    });
    return response.data;
  } catch (error) {
    console.error('Failed to translate salary PDF:', error);
    throw error;
  }
};

/**
 * Fetches the static list of placeholder tokens supported by the custom
 * document template engine (shown to users authoring their DOCX templates).
 */
export const getCustomTemplateTokens = async () => {
  try {
    const response = await axiosClient.get(
      '/companies/custom-templates/tokens'
    );
    return response.data;
  } catch (error) {
    console.error('Failed to fetch custom template tokens:', error);
    throw error;
  }
};

/**
 * Uploads a DOCX file as the custom template for a given documentType on the
 * specified company. Replaces any existing template for that type.
 */
export const uploadCompanyCustomTemplate = async (
  companyId,
  documentType,
  file
) => {
  const formData = new FormData();
  formData.append('template', file);
  const response = await axiosClient.post(
    `/companies/${companyId}/custom-templates/${documentType}`,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } }
  );
  return response.data;
};

export const deleteCompanyCustomTemplate = async (companyId, documentType) => {
  const response = await axiosClient.delete(
    `/companies/${companyId}/custom-templates/${documentType}`
  );
  return response.data;
};

export const downloadCompanyCustomTemplate = async (
  companyId,
  documentType,
  filename = 'template.docx'
) => {
  const response = await axiosClient.get(
    `/companies/${companyId}/custom-templates/${documentType}/download`,
    { responseType: 'blob' }
  );
  const blob = new Blob([response.data], {
    type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

const CompaniesService = {
  createNewCompany,
  retrieveAllCompanies,
  retrieveDropdownAllCompanies,
  getCompanyById,
  updateCompanyById,
  deleteCompanyById,
  createQuestionnaire,
  downloadQuestionnairePdf,
  createStatuaryDocuments,
  createFunctionaryOwnershipDocuments,
  uploadSalaryTranslation,
  getCustomTemplateTokens,
  uploadCompanyCustomTemplate,
  deleteCompanyCustomTemplate,
  downloadCompanyCustomTemplate,
};

export default CompaniesService;
